/**
 * Google Maps 導航工具函數
 * 修復彈出視窗阻擋和地理位置權限問題
 * 整合會話級位置權限管理
 */

import { LocationData, LocationSource, SessionLocationData, LocationPermissionStatus, LocationPermissionChoice } from '../types/CustomType';
import { LOCATION_ACCURACY_CONFIG } from '../configuration';
import { devLog } from './devLogger';

interface NavigationOptions {
  destinationAddress: string;
  timeout?: number; // 地理位置獲取超時時間 (毫秒)
  fallbackToAddressOnly?: boolean; // 是否在無法獲取位置時直接跳轉到景點
}

/**
 * 跳轉到 Google Maps 進行導航
 * @param options 導航選項
 */
export const navigateToGoogleMaps = (options: NavigationOptions): void => {
  const { destinationAddress, timeout = 5000, fallbackToAddressOnly = true } = options;
  
  // 如果不支援地理位置 API，直接跳轉到目的地
  if (!navigator.geolocation) {
    jumpToDestination(destinationAddress);
    return;
  }

  // eslint-disable-next-line prefer-const
  let watchId: number | undefined;

  // 設置超時處理
  const handleTimeout = () => {
    if (watchId !== undefined) {
      navigator.geolocation.clearWatch(watchId);
    }
    
    if (fallbackToAddressOnly) {
      jumpToDestination(destinationAddress);
    }
  };

  const timeoutHandle = setTimeout(handleTimeout, timeout);

  // 嘗試獲取用戶位置
  watchId = navigator.geolocation.watchPosition(
    (position) => {
      // 成功獲取位置
      clearTimeout(timeoutHandle);
      if (watchId !== undefined) {
        navigator.geolocation.clearWatch(watchId);
      }
      
      const { latitude, longitude } = position.coords;
      const originStr = `origin=${latitude},${longitude}`;
      const destStr = `destination=${destinationAddress}`;
      const parameters = `${originStr}&${destStr}&sensor=false`;
      const url = `https://www.google.com/maps/dir/?api=1&${parameters}`;
      
      // 使用 location.href 而不是 window.open 避免彈出視窗阻擋
      window.location.href = url;
    },
    (error) => {
      // 位置獲取失敗
      clearTimeout(timeoutHandle);
      if (watchId !== undefined) {
        navigator.geolocation.clearWatch(watchId);
      }
      
      console.warn('地理位置獲取失敗:', error.message);
      
      if (fallbackToAddressOnly) {
        jumpToDestination(destinationAddress);
      }
    },
    {
      enableHighAccuracy: true,
      timeout: timeout,
      maximumAge: 60000 // 使用 1 分鐘內的快取位置
    }
  );
};

/**
 * 直接跳轉到景點位置（無起點）
 * @param destinationAddress 目的地地址
 */
export const jumpToDestination = (destinationAddress: string): void => {
  const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(destinationAddress)}`;
  window.location.href = url;
};

/**
 * 整合位置權限管理的 Google Maps 導航函數
 * @param event React 點擊事件
 * @param destinationAddress 目的地地址
 */
export const handleGoogleMapClick = (
  event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  destinationAddress: string
): void => {
  event.preventDefault();
  
  // 檢查是否有可用的位置資訊
  const sessionLocation = getLocationSession();

  if (sessionLocation && sessionLocation.source === 'gps') {
    // 有GPS定位：使用精確位置進行導航
    const originStr = `origin=${sessionLocation.lat},${sessionLocation.lng}`;
    const destStr = `destination=${destinationAddress}`;
    const parameters = `${originStr}&${destStr}&sensor=false`;
    const url = `https://www.google.com/maps/dir/?api=1&${parameters}`;
    window.location.href = url;
  } else {
    // 無GPS定位或使用預設位置：直接跳轉到目的地
    jumpToDestination(destinationAddress);
  }
};

/**
 * 為向後兼容性保留的舊式處理函數（已更新以整合權限檢查）
 * @param event React 點擊事件
 * @param destinationAddress 目的地地址
 */
export const handleGoogleMapClickLegacy = (
  event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  destinationAddress: string
): void => {
  event.preventDefault();
  
  navigateToGoogleMaps({
    destinationAddress,
    timeout: 5000,
    fallbackToAddressOnly: true
  });
};

/**
 * 位置權限管理相關工具函數
 */

// Session Storage Key
const YJ_AUTO_LOCATION_SESSION = 'YJ_AUTO_LOCATION_SESSION';

// 永靖鄉預設座標 (更精確的座標)
const YONGJING_DEFAULT_LOCATION = {
  lat: 23.9268,
  lng: 120.5676
};

// 精度閾值 (公尺)
const ACCURACY_THRESHOLD = 50;

/**
 * 檢測瀏覽器是否支援 Geolocation API
 */
export const checkGeolocationSupport = (): boolean => {
  const hasGeolocation = 'geolocation' in navigator;
  const hasNavigator = typeof navigator !== 'undefined';

  devLog('🔍 位置支援檢查:', {
    hasNavigator,
    hasGeolocation,
    userAgent: navigator.userAgent,
    protocol: window.location.protocol,
    isSecureContext: window.isSecureContext
  });

  return hasNavigator && hasGeolocation;
};

/**
 * 自動定位請求（5秒逾時）
 */
export const requestAutoLocation = (): Promise<LocationData> => {
  return new Promise((resolve) => {
    devLog('🎯 開始自動定位請求...');

    if (!checkGeolocationSupport()) {
      devLog('🚫 瀏覽器不支援地理位置 API，使用預設位置');
      resolve(getYongJingDefaultLocation());
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const accuracy = position.coords.accuracy;

        devLog(`📍 GPS定位成功: 緯度=${lat}, 經度=${lng}, 精度=${accuracy}m`);

        const locationData = validateLocationAccuracy({
          lat,
          lng,
          accuracy,
          source: 'gps' as LocationSource,
          timestamp: Date.now()
        });

        resolve(locationData);
      },
      (error) => {
        console.error(`❌ 自動定位失敗: ${error.message} (錯誤代碼: ${error.code})`);
        devLog('🏠 定位失敗，使用永靖鄉預設位置');
        resolve(getYongJingDefaultLocation());
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 60000
      }
    );
  });
};

/**
 * 驗證定位精度（50公尺閾值）
 */
export const validateLocationAccuracy = (location: LocationData): LocationData => {
  if (location.accuracy && location.accuracy > ACCURACY_THRESHOLD) {
    devLog(`⚠️ 定位精度不足: ${location.accuracy}m > ${ACCURACY_THRESHOLD}m，改用預設位置`);
    return getYongJingDefaultLocation();
  }

  devLog(`✅ 定位精度符合要求: ${location.accuracy}m <= ${ACCURACY_THRESHOLD}m`);
  return location;
};

/**
 * 獲取永靖鄉預設位置
 */
export const getYongJingDefaultLocation = (): LocationData => {
  devLog(`🏠 使用永靖鄉預設位置: 緯度=${YONGJING_DEFAULT_LOCATION.lat}, 經度=${YONGJING_DEFAULT_LOCATION.lng}`);
  return {
    lat: YONGJING_DEFAULT_LOCATION.lat,
    lng: YONGJING_DEFAULT_LOCATION.lng,
    accuracy: null,
    source: 'default',
    timestamp: Date.now()
  };
};

/**
 * 儲存位置到 sessionStorage
 */
export const saveLocationSession = (location: LocationData): void => {
  try {
    const sessionData: SessionLocationData = {
      location,
      sessionId: generateSessionId()
    };
    sessionStorage.setItem(YJ_AUTO_LOCATION_SESSION, JSON.stringify(sessionData));
    devLog('💾 位置資訊已儲存到 sessionStorage');
  } catch (error) {
    console.warn('無法儲存位置資訊到 sessionStorage:', error);
  }
};

/**
 * 從 sessionStorage 讀取位置設定
 */
export const getLocationSession = (): LocationData | null => {
  try {
    const stored = sessionStorage.getItem(YJ_AUTO_LOCATION_SESSION);
    if (stored) {
      const sessionData: SessionLocationData = JSON.parse(stored);
      devLog('📱 從 sessionStorage 讀取位置資訊');
      return sessionData.location;
    }
  } catch (error) {
    console.warn('無法從 sessionStorage 讀取位置資訊:', error);
  }
  return null;
};

/**
 * 清除 sessionStorage 中的位置設定
 */
export const clearLocationSession = (): void => {
  try {
    sessionStorage.removeItem(YJ_AUTO_LOCATION_SESSION);
    devLog('🗑️ 已清除 sessionStorage 中的位置資訊');
  } catch (error) {
    console.warn('無法清除 sessionStorage 中的位置資訊:', error);
  }
};

/**
 * 生成會話ID
 */
const generateSessionId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

/**
 * 檢查位置服務的安全要求
 */
export const checkLocationSecurityRequirements = (): { 
  isSecure: boolean; 
  issues: string[]; 
  canUseLocation: boolean; 
} => {
  const issues: string[] = [];
  
  // 檢查 HTTPS 要求
  const isHttps = window.location.protocol === 'https:';
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const isSecureContext = window.isSecureContext;
  
  if (!isSecureContext) {
    if (!isHttps && !isLocalhost) {
      issues.push('需要 HTTPS 連線才能使用位置服務（localhost 除外）');
    } else {
      issues.push('當前上下文不被認為是安全的');
    }
  }
  
  // 檢查瀏覽器支援
  if (!('geolocation' in navigator)) {
    issues.push('瀏覽器不支援 Geolocation API');
  }
  
  // 檢查權限 API 支援
  if (!('permissions' in navigator)) {
    issues.push('瀏覽器不支援 Permissions API（可能會影響權限狀態檢查）');
  }
  
  const canUseLocation = isSecureContext && ('geolocation' in navigator);
  
  devLog('🔒 位置服務安全檢查:', {
    protocol: window.location.protocol,
    hostname: window.location.hostname,
    isHttps,
    isLocalhost,
    isSecureContext,
    hasGeolocation: 'geolocation' in navigator,
    hasPermissions: 'permissions' in navigator,
    canUseLocation,
    issues
  });
  
  return {
    isSecure: isSecureContext,
    issues,
    canUseLocation
  };
};

/**
 * 請求位置權限（實際觸發瀏覽器權限請求）
 */
export const requestLocationPermission = (): Promise<LocationPermissionStatus> => {
  return new Promise((resolve) => {
    devLog('🔐 開始請求位置權限...');
    
    // 1. 先檢查安全要求
    const securityCheck = checkLocationSecurityRequirements();
    if (!securityCheck.canUseLocation) {
      console.error('❌ 位置服務安全檢查失敗:', securityCheck.issues);
      securityCheck.issues.forEach(issue => console.error(`  - ${issue}`));
      resolve(LocationPermissionStatus.DENIED);
      return;
    }
    
    // 2. 檢查地理位置 API 支援
    if (!checkGeolocationSupport()) {
      devLog('🚫 瀏覽器不支援地理位置 API，權限被拒絕');
      resolve(LocationPermissionStatus.DENIED);
      return;
    }

    // 3. 首先嘗試檢查現有權限狀態（不觸發權限請求）
    if ('permissions' in navigator && navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        devLog('🔍 當前權限狀態:', result.state);
        
        if (result.state === 'granted') {
          devLog('✅ 位置權限已經被允許');
          resolve(LocationPermissionStatus.GRANTED);
          return;
        } else if (result.state === 'denied') {
          devLog('🚫 位置權限已經被拒絕');
          resolve(LocationPermissionStatus.DENIED);
          return;
        }
        
        // 如果狀態是 'prompt'，繼續嘗試獲取位置以觸發權限對話框
        performLocationRequest();
      }).catch(() => {
        // 權限查詢失敗，直接嘗試位置請求
        devLog('⚠️ 權限狀態查詢失敗，直接嘗試位置請求');
        performLocationRequest();
      });
    } else {
      // 不支援權限 API，直接嘗試位置請求
      devLog('⚠️ 不支援權限 API，直接嘗試位置請求');
      performLocationRequest();
    }
    
    // 4. 執行位置請求的核心函數
    function performLocationRequest() {
      const geolocation = navigator.geolocation;
      if (!geolocation) {
        console.error('🚫 Geolocation API 不存在');
        resolve(LocationPermissionStatus.DENIED);
        return;
      }

      devLog('📍 開始獲取位置以觸發權限對話框...');
      
      // 設置請求參數 - 使用高精確度以確保定位準確性
      const options: PositionOptions = {
        enableHighAccuracy: true, // 使用高精確度定位，確保與 Google Maps 一致
        timeout: 15000,          // 15 秒超時，給 GPS 更多時間
        maximumAge: 60000        // 1 分鐘內的快取位置可用（減少重複請求）
      };
      
      geolocation.getCurrentPosition(
        (position) => {
          // 成功獲取位置，表示權限被允許
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const accuracy = position.coords.accuracy;
          const timestamp = new Date(position.timestamp).toLocaleTimeString();
          
          devLog(`✅ 位置權限獲得成功！`, {
            緯度: lat,
            經度: lng,
            精確度: `${accuracy}m`,
            時間戳: timestamp
          });
          
          resolve(LocationPermissionStatus.GRANTED);
        },
        (error) => {
          // 獲取位置失敗，根據錯誤類型判斷權限狀態
          console.error(`❌ 位置權限請求失敗:`, {
            錯誤代碼: error.code,
            錯誤訊息: error.message,
            時間戳: new Date().toLocaleTimeString()
          });
          
          // 提供詳細的錯誤分析
          let errorAnalysis = '';
          let permissionStatus = LocationPermissionStatus.UNKNOWN;
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorAnalysis = '用戶明確拒絕了位置權限，或瀏覽器政策阻止了位置存取';
              permissionStatus = LocationPermissionStatus.DENIED;
              devLog('🚫 ' + errorAnalysis);
              
              // 提供解決方案建議
              devLog('💡 解決方案:');
              devLog('  1. 檢查瀏覽器位址列是否有位置權限圖示，點擊重新允許');
              devLog('  2. 檢查瀏覽器設定中的位置權限');
              devLog('  3. 確保使用 HTTPS 連線');
              break;
              
            case error.POSITION_UNAVAILABLE:
              errorAnalysis = 'GPS 或網路定位服務暫時不可用，但權限可能已允許';
              permissionStatus = LocationPermissionStatus.GRANTED; // 假設權限已允許，只是定位失敗
              devLog('📍 ' + errorAnalysis);
              
              devLog('💡 可能原因:');
              devLog('  1. GPS 訊號弱或室內環境');
              devLog('  2. 網路連線問題');
              devLog('  3. 位置服務暫時故障');
              break;
              
            case error.TIMEOUT:
              errorAnalysis = '位置權限請求或定位過程超時（12秒）';
              permissionStatus = LocationPermissionStatus.UNKNOWN;
              devLog('⏰ ' + errorAnalysis);
              
              devLog('💡 可能原因:');
              devLog('  1. 網路連線緩慢');
              devLog('  2. GPS 訊號搜尋時間過長');
              devLog('  3. 裝置性能問題');
              break;
              
            default:
              errorAnalysis = `未預期的位置權限錯誤 (錯誤代碼: ${error.code})`;
              permissionStatus = LocationPermissionStatus.UNKNOWN;
              devLog('❓ ' + errorAnalysis);
              break;
          }
          
          // 記錄完整的調試資訊
          devLog('🔍 詳細錯誤資訊:', {
            錯誤代碼: error.code,
            錯誤訊息: error.message,
            錯誤常數: {
              PERMISSION_DENIED: error.PERMISSION_DENIED,
              POSITION_UNAVAILABLE: error.POSITION_UNAVAILABLE,
              TIMEOUT: error.TIMEOUT
            },
            分析結果: errorAnalysis,
            權限狀態: permissionStatus,
            瀏覽器資訊: {
              userAgent: navigator.userAgent.substring(0, 100) + '...',
              語言: navigator.language,
              平台: navigator.platform
            },
            環境資訊: {
              協議: window.location.protocol,
              域名: window.location.hostname,
              安全上下文: window.isSecureContext
            }
          });
          
          resolve(permissionStatus);
        },
        options
      );
    }
  });
};

/**
 * 檢查位置權限狀態（不觸發權限請求）
 */
export const checkLocationPermissionStatus = (): Promise<LocationPermissionStatus> => {
  return new Promise((resolve) => {
    if (!checkGeolocationSupport()) {
      resolve(LocationPermissionStatus.DENIED);
      return;
    }

    // 檢查現有權限狀態
    if ('permissions' in navigator && navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        switch (result.state) {
          case 'granted':
            resolve(LocationPermissionStatus.GRANTED);
            break;
          case 'denied':
            resolve(LocationPermissionStatus.DENIED);
            break;
          case 'prompt':
            resolve(LocationPermissionStatus.PROMPT);
            break;
          default:
            resolve(LocationPermissionStatus.UNKNOWN);
        }
      }).catch(() => {
        resolve(LocationPermissionStatus.UNKNOWN);
      });
    } else {
      resolve(LocationPermissionStatus.UNKNOWN);
    }
  });
};

/**
 * 獲取用戶當前位置
 */
export const getCurrentUserLocation = (): Promise<GeolocationPosition | null> => {
  return new Promise((resolve) => {
    if (!checkGeolocationSupport()) {
      devLog('🚫 瀏覽器不支援地理位置 API');
      resolve(null);
      return;
    }

    devLog('📍 開始獲取用戶位置...');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const accuracy = position.coords.accuracy;
        devLog(`✅ 成功獲取用戶位置: 緯度=${lat}, 經度=${lng}, 精確度=${accuracy}m`);
        resolve(position);
      },
      (error) => {
        console.error(`❌ 獲取用戶位置失敗: ${error.message} (錯誤代碼: ${error.code})`);
        resolve(null);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 60000
      }
    );
  });
};

/**
 * 獲取高精確度用戶位置（精確度小於指定值）
 * 最多重試指定次數，如果最終仍未達到目標精確度則返回 null
 */
export const getHighAccuracyUserLocation = (
  targetAccuracy = 50,
  maxRetries = 5,
  retryDelay = 1000
): Promise<GeolocationPosition | null> => {
  return new Promise((resolve) => {
    if (!checkGeolocationSupport()) {
      devLog('🚫 瀏覽器不支援地理位置 API');
      resolve(null);
      return;
    }

    devLog(`🎯 開始獲取高精確度位置 (目標精確度: ${targetAccuracy}m, 最多重試: ${maxRetries}次)`);

    const tryGetLocation = async (attempt: number): Promise<void> => {
      devLog(`📍 第 ${attempt}/${maxRetries} 次定位嘗試...`);

      try {
        // 第一次嘗試使用較長的超時時間，之後的嘗試逐漸增加超時
        const timeoutMs = attempt === 1 ? LOCATION_ACCURACY_CONFIG.HIGH_ACCURACY_TIMEOUT : 
                         LOCATION_ACCURACY_CONFIG.HIGH_ACCURACY_TIMEOUT + (attempt - 1) * 2000;
        const position = await getCurrentUserLocationWithTimeout(timeoutMs);
        
        if (position) {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const accuracy = position.coords.accuracy;

          devLog(`📊 第 ${attempt} 次定位結果: 緯度=${lat}, 經度=${lng}, 精確度=${accuracy}m`);

          if (accuracy <= targetAccuracy) {
            devLog(`✅ 達到目標精確度！最終位置: 緯度=${lat}, 經度=${lng}, 精確度=${accuracy}m`);
            resolve(position);
            return;
          } else {
            devLog(`⚠️ 精確度 ${accuracy}m 超過目標 ${targetAccuracy}m，準備重新定位...`);
            
            if (attempt < maxRetries) {
              devLog(`⏳ 等待 ${retryDelay}ms 後重新嘗試...`);
              setTimeout(() => {
                tryGetLocation(attempt + 1);
              }, retryDelay);
            } else {
              devLog(`❌ 已達最大重試次數 ${maxRetries} 次，精確度 ${accuracy}m 仍超過目標 ${targetAccuracy}m`);
              devLog(`🏠 精確度不足，將使用預設位置代替`);
              resolve(null); // 返回 null，讓調用者使用預設位置
            }
          }
        } else {
          console.warn(`❌ 第 ${attempt} 次定位失敗，無法獲取位置`);
          if (attempt < maxRetries) {
            setTimeout(() => {
              tryGetLocation(attempt + 1);
            }, retryDelay);
          } else {
            devLog('❌ 所有定位嘗試都失敗，返回 null');
            resolve(null);
          }
        }
      } catch (error) {
        console.error(`❌ 第 ${attempt} 次定位發生錯誤:`, error);
        if (attempt < maxRetries) {
          setTimeout(() => {
            tryGetLocation(attempt + 1);
          }, retryDelay);
        } else {
          resolve(null);
        }
      }
    };

    // 開始第一次嘗試
    tryGetLocation(1);
  });
};

/**
 * 帶超時的位置獲取
 */
const getCurrentUserLocationWithTimeout = (timeout = 8000): Promise<GeolocationPosition | null> => {
  return new Promise((resolve) => {
    if (!checkGeolocationSupport()) {
      console.error('🚫 瀏覽器不支援地理位置 API');
      resolve(null);
      return;
    }

    devLog(`📍 開始獲取位置，超時設定: ${timeout}ms`);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const accuracy = position.coords.accuracy;
        const timestamp = new Date(position.timestamp).toLocaleTimeString();
        
        devLog(`✅ 位置獲取成功:`, {
          latitude: lat,
          longitude: lng,
          accuracy: `${accuracy}m`,
          timestamp,
          altitude: position.coords.altitude,
          altitudeAccuracy: position.coords.altitudeAccuracy,
          heading: position.coords.heading,
          speed: position.coords.speed
        });
        
        resolve(position);
      },
      (error) => {
        const errorDetails = {
          code: error.code,
          message: error.message,
          timestamp: new Date().toLocaleTimeString()
        };
        
        let errorDescription = '';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorDescription = '用戶拒絕了地理位置權限';
            break;
          case error.POSITION_UNAVAILABLE:
            errorDescription = '地理位置資訊無法取得';
            break;
          case error.TIMEOUT:
            errorDescription = `獲取地理位置超時 (${timeout}ms)`;
            break;
          default:
            errorDescription = '未知的地理位置錯誤';
            break;
        }
        
        console.error(`❌ 位置獲取失敗:`, { ...errorDetails, description: errorDescription });
        resolve(null);
      },
      {
        enableHighAccuracy: true,  // 啟用高精確度
        timeout,                   // 設定超時  
        maximumAge: 0             // 不使用快取，強制重新定位以獲得最新最準確位置
      }
    );
  });
};

/**
 * 智能位置獲取 - 總時間控制在3秒內的快速定位
 * 優先速度，確保用戶體驗流暢
 */
export const getSmartUserLocation = async (
  targetAccuracy = LOCATION_ACCURACY_CONFIG.TARGET_ACCURACY
): Promise<GeolocationPosition | null> => {
  devLog('⚡ 開始快速智能位置獲取 (總時限: 3秒)...');
  
  if (!checkGeolocationSupport()) {
    devLog('🚫 瀏覽器不支援地理位置 API');
    return null;
  }

  const startTime = Date.now();
  const totalTimeout = LOCATION_ACCURACY_CONFIG.TOTAL_LOCATION_TIMEOUT;
  
  try {
    let bestPosition: GeolocationPosition | null = null;
    let bestAccuracy = Infinity;
    const maxAttempts = LOCATION_ACCURACY_CONFIG.MAX_RETRIES;
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const elapsed = Date.now() - startTime;
      const remaining = totalTimeout - elapsed;
      
      if (remaining < 500) { // 如果剩餘時間不足500ms，直接退出
        devLog(`⏰ 剩餘時間不足 ${remaining}ms，結束定位嘗試`);
        break;
      }
      
      devLog(`🎯 第 ${attempt}/${maxAttempts} 次快速定位 (剩餘時間: ${remaining}ms)...`);
      
      // 根據剩餘時間動態調整單次嘗試的超時時間
      const singleTimeout = Math.min(
        remaining - 100, // 預留100ms給結果處理
        LOCATION_ACCURACY_CONFIG.HIGH_ACCURACY_TIMEOUT
      );
      
      const position = await getQuickLocationAttempt(singleTimeout);
      
      if (position) {
        const accuracy = position.coords.accuracy;
        const currentElapsed = Date.now() - startTime;
        devLog(`📊 第 ${attempt} 次結果: 精確度=${accuracy}m (用時: ${currentElapsed}ms)`);
        
        // 更新最佳結果
        if (accuracy < bestAccuracy) {
          bestPosition = position;
          bestAccuracy = accuracy;
          devLog(`✨ 更新最佳位置: 精確度=${accuracy}m`);
        }
        
        // 如果達到目標精確度，立即返回
        if (accuracy <= targetAccuracy) {
          devLog(`🎉 達到目標精確度！最終位置: 精確度=${accuracy}m (總用時: ${currentElapsed}ms)`);
          return position;
        }
      } else {
        devLog(`❌ 第 ${attempt} 次定位失敗`);
      }
      
      // 檢查是否還有時間進行下次嘗試
      if (attempt < maxAttempts) {
        const currentElapsed = Date.now() - startTime;
        const timeLeft = totalTimeout - currentElapsed;
        
        if (timeLeft < 800) { // 如果剩餘時間不足以進行另一次嘗試，直接結束
          devLog(`⏰ 時間不足 (${timeLeft}ms)，終止後續嘗試`);
          break;
        }
        
        // 短暫等待
        await new Promise(resolve => setTimeout(resolve, LOCATION_ACCURACY_CONFIG.RETRY_DELAY));
      }
    }
    
    const totalElapsed = Date.now() - startTime;
    devLog(`⏱️ 定位總耗時: ${totalElapsed}ms`);
    
    if (bestPosition) {
      devLog(`📍 評估最佳位置結果: 精確度=${bestAccuracy}m`);
      
      // 如果精確度超過目標值，拒絕使用
      if (bestAccuracy > targetAccuracy) {
        devLog(`❌ 精確度 ${bestAccuracy}m 超過目標 ${targetAccuracy}m，將使用預設位置`);
        devLog(`🏠 改用永靖鄉預設位置以確保地圖顯示準確性`);
        return null;
      } else {
        devLog(`🎯 位置精確度符合要求：${bestAccuracy}m <= ${targetAccuracy}m`);
        return bestPosition;
      }
    } else {
      devLog('❌ 所有定位嘗試都失敗，將使用預設位置');
      return null;
    }
    
  } catch (error) {
    console.error('❌ 智能位置獲取發生錯誤:', error);
    return null;
  }
};

/**
 * 快速位置獲取嘗試 - 針對3秒總時限優化
 */
const getQuickLocationAttempt = async (
  timeoutMs: number
): Promise<GeolocationPosition | null> => {
  return new Promise((resolve) => {
    const options: PositionOptions = {
      enableHighAccuracy: true,    // 使用高精確度
      timeout: timeoutMs,          // 動態超時時間
      maximumAge: 5000            // 允許使用5秒內的快取結果，提高速度
    };
    
    devLog(`⚡ 快速定位參數: 超時=${timeoutMs}ms, 快取=5s`);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const accuracy = position.coords.accuracy;
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        devLog(`📍 快速定位成功:`, {
          緯度: lat.toFixed(6),
          經度: lng.toFixed(6),
          精確度: `${accuracy}m`
        });
        
        resolve(position);
      },
      (error) => {
        devLog(`⚠️ 快速定位失敗: ${getErrorTypeName(error.code)} - ${error.message}`);
        resolve(null);
      },
      options
    );
  });
};

/**
 * 優化的高精確度位置獲取 - 使用更積極的 GPS 參數（保留原函數以向後兼容）
 */
const getHighAccuracyUserLocationWithBetterOptions = async (
  targetAccuracy = LOCATION_ACCURACY_CONFIG.TARGET_ACCURACY,
  attempt = 1
): Promise<GeolocationPosition | null> => {
  // 為了向後兼容保留此函數，但使用快速版本
  return getQuickLocationAttempt(LOCATION_ACCURACY_CONFIG.HIGH_ACCURACY_TIMEOUT);
};

/**
 * 獲取錯誤類型名稱
 */
const getErrorTypeName = (code: number): string => {
  switch (code) {
    case 1: return 'PERMISSION_DENIED';
    case 2: return 'POSITION_UNAVAILABLE';
    case 3: return 'TIMEOUT';
    default: return 'UNKNOWN';
  }
};

/**
 * 位置服務診斷函數
 */
export const diagnoseLocationService = async (): Promise<void> => {
  devLog('🔍 開始位置服務診斷...');
  
  // 1. 檢查基本支援
  devLog('📋 基本環境檢查:');
  devLog(`  - Navigator 存在: ${typeof navigator !== 'undefined'}`);
  devLog(`  - Geolocation API: ${'geolocation' in navigator}`);
  devLog(`  - 安全上下文: ${window.isSecureContext}`);
  devLog(`  - 協議: ${window.location.protocol}`);
  devLog(`  - 域名: ${window.location.hostname}`);
  devLog(`  - User Agent: ${navigator.userAgent}`);
  
  // 2. 檢查權限狀態
  if ('permissions' in navigator) {
    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' });
      devLog(`  - 權限狀態: ${permission.state}`);
    } catch (error) {
      devLog(`  - 權限查詢失敗: ${error}`);
    }
  } else {
    devLog('  - 不支援權限 API');
  }
  
  // 3. 檢查連線狀態
  if ('onLine' in navigator) {
    devLog(`  - 網路狀態: ${navigator.onLine ? '已連線' : '離線'}`);
  }
  
  // 4. 嘗試快速位置測試
  if ('geolocation' in navigator) {
    devLog('📍 嘗試快速位置測試...');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        devLog('✅ 快速位置測試成功:', {
          緯度: position.coords.latitude,
          經度: position.coords.longitude,
          精確度: `${position.coords.accuracy}m`,
          時間戳: new Date(position.timestamp).toLocaleString()
        });
      },
      (error) => {
        console.error('❌ 快速位置測試失敗:', {
          錯誤代碼: error.code,
          錯誤訊息: error.message,
          PERMISSION_DENIED: error.PERMISSION_DENIED,
          POSITION_UNAVAILABLE: error.POSITION_UNAVAILABLE,
          TIMEOUT: error.TIMEOUT
        });
      },
      {
        enableHighAccuracy: true, // 診斷測試也使用高精確度，更真實反映使用情況
        timeout: 8000,           // 增加超時時間給 GPS 定位
        maximumAge: 30000        // 30 秒內快取有效，適合診斷測試
      }
    );
  }
  
  devLog('🔍 位置服務診斷完成');
};

/**
 * 獲取永靖鄉預設位置（已棄用，請使用 getYongJingDefaultLocation）
 * @deprecated 請使用 getYongJingDefaultLocation 替代
 */
export const getDefaultLocation = () => {
  console.warn('getDefaultLocation 已棄用，請使用 getYongJingDefaultLocation');
  return getYongJingDefaultLocation();
};

/**
 * 儲存位置權限選擇到 sessionStorage（已棄用）
 * @deprecated 自動定位系統不需要儲存權限選擇
 */
export const saveLocationPermissionSession = (choice: LocationPermissionChoice): void => {
  console.warn('saveLocationPermissionSession 已棄用，自動定位系統不需要儲存權限選擇');
  // 不再執行任何操作
};

/**
 * 從 sessionStorage 讀取位置權限設定（已棄用）
 * @deprecated 自動定位系統不需要讀取權限選擇
 */
export const getLocationPermissionSession = (): LocationPermissionChoice | null => {
  console.warn('getLocationPermissionSession 已棄用，自動定位系統不需要讀取權限選擇');
  return null;
};

/**
 * 清除 sessionStorage 中的位置權限設定（已棄用）
 * @deprecated 自動定位系統不需要清除權限選擇
 */
export const clearLocationPermissionSession = (): void => {
  console.warn('clearLocationPermissionSession 已棄用，自動定位系統不需要清除權限選擇');
  // 不再執行任何操作
};

/**
 * 錯誤處理和降級服務
 */

// 記憶體暫存作為 sessionStorage 的降級方案
let memoryLocationPermissionCache: LocationPermissionChoice | null = null;

/**
 * 檢查 sessionStorage 是否可用
 */
const isSessionStorageAvailable = (): boolean => {
  try {
    const testKey = 'yj_test_storage';
    sessionStorage.setItem(testKey, 'test');
    sessionStorage.removeItem(testKey);
    return true;
  } catch (error) {
    console.warn('sessionStorage 不可用，將使用記憶體暫存:', error);
    return false;
  }
};

/**
 * 強化版儲存位置權限選擇（包含降級處理）
 */
export const saveLocationPermissionSessionWithFallback = (choice: LocationPermissionChoice): void => {
  try {
    if (isSessionStorageAvailable()) {
      saveLocationPermissionSession(choice);
    } else {
      // 降級使用記憶體暫存
      memoryLocationPermissionCache = choice;
      console.info('使用記憶體暫存儲存位置權限設定:', choice);
    }
  } catch (error) {
    // 雙重錯誤處理
    console.error('儲存位置權限設定時發生錯誤:', error);
    memoryLocationPermissionCache = choice;
    console.info('降級使用記憶體暫存:', choice);
  }
};

/**
 * 強化版讀取位置權限設定（包含降級處理）
 */
export const getLocationPermissionSessionWithFallback = (): LocationPermissionChoice | null => {
  try {
    if (isSessionStorageAvailable()) {
      const sessionChoice = getLocationPermissionSession();
      if (sessionChoice !== null) {
        return sessionChoice;
      }
    }
    
    // 降級讀取記憶體暫存
    return memoryLocationPermissionCache;
  } catch (error) {
    console.error('讀取位置權限設定時發生錯誤:', error);
    return memoryLocationPermissionCache;
  }
};

/**
 * 網路連線檢查
 */
export const isOnline = (): boolean => {
  return navigator.onLine;
};

/**
 * 帶重試機制的位置獲取
 */
export const getCurrentUserLocationWithRetry = async (
  maxRetries = 2,
  timeoutMs = 5000
): Promise<GeolocationPosition | null> => {
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      devLog(`嘗試獲取位置 (${attempt}/${maxRetries})`);
      
      // 檢查網路連線
      if (!isOnline()) {
        throw new Error('網路連線不可用');
      }
      
      const position = await getCurrentUserLocation();
      if (position) {
        devLog('位置獲取成功');
        return position;
      }
      
      throw new Error('位置獲取返回 null');
    } catch (error) {
      console.warn(`位置獲取嘗試 ${attempt} 失敗:`, error);
      
      // 如果不是最後一次嘗試，等待後重試
      if (attempt < maxRetries) {
        const delay = attempt * 1000; // 遞增延遲
        devLog(`等待 ${delay}ms 後重試...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  console.error('所有位置獲取嘗試都失敗，使用預設位置');
  return null;
};

/**
 * 錯誤日誌記錄
 */
interface LocationErrorLog {
  timestamp: number;
  error: string;
  context: string;
  userAgent: string;
  url: string;
}

const locationErrorLogs: LocationErrorLog[] = [];
const MAX_ERROR_LOGS = 50;

export const logLocationError = (error: Error | string, context: string): void => {
  const errorLog: LocationErrorLog = {
    timestamp: Date.now(),
    error: error.toString(),
    context,
    userAgent: navigator.userAgent,
    url: window.location.href
  };
  
  locationErrorLogs.push(errorLog);
  
  // 保持日誌數量在限制內
  if (locationErrorLogs.length > MAX_ERROR_LOGS) {
    locationErrorLogs.shift();
  }
  
  console.error(`[位置權限錯誤] ${context}:`, error);
};

/**
 * 獲取錯誤日誌（用於調試）
 */
export const getLocationErrorLogs = (): LocationErrorLog[] => {
  return [...locationErrorLogs];
};

/**
 * 清除錯誤日誌
 */
export const clearLocationErrorLogs = (): void => {
  locationErrorLogs.length = 0;
};

/**
 * 綜合錯誤處理的地理位置獲取函數
 */
export const getLocationWithFullErrorHandling = async (): Promise<{
  position: GeolocationPosition | null;
  error: string | null;
  fallbackUsed: boolean;
}> => {
  try {
    // 檢查基本支援
    if (!checkGeolocationSupport()) {
      const error = '瀏覽器不支援地理位置 API';
      logLocationError(error, 'getLocationWithFullErrorHandling');
      return {
        position: null,
        error,
        fallbackUsed: true
      };
    }
    
    // 檢查權限設定
    const permissionChoice = getLocationPermissionSessionWithFallback();
    if (permissionChoice === LocationPermissionChoice.DENY || permissionChoice === LocationPermissionChoice.LATER) {
      return {
        position: null,
        error: '用戶已拒絕或選擇稍後決定位置權限',
        fallbackUsed: true
      };
    }
    
    // 嘗試獲取位置（帶重試）
    const position = await getCurrentUserLocationWithRetry(3, 8000);
    
    if (position) {
      return {
        position,
        error: null,
        fallbackUsed: false
      };
    } else {
      const error = '無法獲取用戶位置';
      logLocationError(error, 'getLocationWithFullErrorHandling');
      return {
        position: null,
        error,
        fallbackUsed: true
      };
    }
  } catch (error) {
    const errorMessage = `位置獲取發生未預期錯誤: ${error}`;
    logLocationError(error as Error, 'getLocationWithFullErrorHandling');
    return {
      position: null,
      error: errorMessage,
      fallbackUsed: true
    };
  }
};
